import {VinRegistrationStates} from '@/utils/constants'

export interface VinStatusResult {
  error?: boolean
  key?: string
  account?: {email?: string}
  hasDevice?: boolean
  allowDeviceReattachment?: boolean
  vinInUse?: boolean
  vehicleId?: string
  registrationId?: string
  hondaPlusEligible?: boolean
}

/**
 * Determines the VIN registration status based on the API response
 * This matches the exact production logic from DialogVinRegistration.vue:148-158
 *
 * Priority order:
 * 1. REATTACHMENT_ALLOWED
 * 2. VIN_NOT_ELIGIBLE (VIN exists but not in Honda whitelist)
 * 3. ALREADY_USED_VIN / REALTA_VIN (with error flag)
 * 4. ALREADY_USED_VIN_REGISTRATION
 * 5. VIN_VALID / NO_VIN_BDD
 * 6. null (unknown status)
 */
export function determineVinStatus(
  result: VinStatusResult,
  {skipEligibilityCheck}: {skipEligibilityCheck?: boolean} = {},
): string | null {
  if (result.key === 'REATTACHMENT_ALLOWED') {
    return VinRegistrationStates.REATTACHMENT_ALLOWED
  } else if (!skipEligibilityCheck && result.hondaPlusEligible === false) {
    return VinRegistrationStates.VIN_NOT_ELIGIBLE
  } else if (result.error && (result.key === 'ALREADY_USED_VIN' || result.key === 'REALTA_VIN')) {
    return result.account?.email
      ? VinRegistrationStates.FOUND_WITH_DEVICE
      : VinRegistrationStates.FOUND_WITHOUT_OWNER_EMAIL
  } else if (result.key === 'ALREADY_USED_VIN_REGISTRATION') {
    return VinRegistrationStates.FOUND_WITH_DEVICE_PENDING
  } else if (result.key === 'VIN_VALID' || result.key === 'NO_VIN_BDD') {
    return VinRegistrationStates.NOT_FOUND
  } else {
    return null
  }
}

/**
 * Validates if a VIN status allows proceeding with registration
 * This matches the production logic from DialogVinRegistration.vue:110
 *
 * Returns true for:
 * - NOT_FOUND (new VIN)
 * - REATTACHMENT_ALLOWED (device was detached from portal)
 *
 * Returns false for:
 * - VIN_NOT_ELIGIBLE (VIN not in Honda whitelist)
 * - FOUND_WITH_DEVICE (already registered)
 * - FOUND_WITHOUT_OWNER_EMAIL (incomplete registration)
 * - FOUND_WITH_DEVICE_PENDING (pending registration)
 * - null or undefined status
 */
export function isValidVin(status: string | null): boolean {
  return (
    !!status &&
    status !== VinRegistrationStates.FOUND_WITH_DEVICE &&
    status !== VinRegistrationStates.FOUND_WITHOUT_OWNER_EMAIL &&
    status !== VinRegistrationStates.FOUND_WITH_DEVICE_PENDING &&
    status !== VinRegistrationStates.VIN_NOT_ELIGIBLE
  )
}
